import {
  GoABlock,
  GoAButton,
  GoAButtonGroup,
  GoACallout,
  GoAModal,
  GoASpacer,
} from '@abgov/react-components';
import { useState } from 'react';
import { RootState } from '../../state/store';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useCsrService from '../../services/csr-api-redux';
import { useAuth } from 'react-oidc-context';
import { albertaCaConfig } from '../../utils/oidcConfig';
import styles from './onboarding.module.css';
import { REACT_CREATE_NEW_USER_URL } from '../../state/configuration';

interface AlbertanLandingComponentProps {}

const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

export const AlbertanLandingComponent = ({}: AlbertanLandingComponentProps) => {
  const navigate = useNavigate();
  const { signinRedirect } = useAuth();
  const stateCollision = useAppSelector((state: RootState) => state.collision);
  const csrService = useCsrService();

  const [openRegisterModal, setOpenRegisterModal] = useState(false);

  const handleLogin = () => {
    signinRedirect({
      ...albertaCaConfig,
    });
  };

  const handleCreateNewAccountClick = () => {
    setOpenRegisterModal(true);
  };

  return (
    <div className="contentbox">
      <GoASpacer hSpacing={'xl'} />
      <div className="contentcolumn">
        <GoASpacer vSpacing={'xl'} />
        <h1>Report My Collision</h1>
        <div>
          <p>
            Report My Collision is an online service that allows Albertans to
            report property damage vehicle collisions online. This online
            service is an alternative to other in-person collision reporting
            options (for example law enforcement locations or collision
            reporting centres, depending on where your collision occurred).
          </p>

          <h3 className={styles.headercontainer}>
            You can use the Report My Collision online service if:
          </h3>
          <ul style={{ margin: '0 0 10px 0' }} className="goa-unordered-list">
            <li>
              You meet the Report My Collision eligibility criteria questions on
              the following page; and
            </li>
            <li>
              The collision you are reporting matches one of the four types of
              collisions listed below
            </li>
          </ul>
        </div>
        <h3 className={styles.headercontainer}>
          What to do in the event of a collision
        </h3>
        <ul style={{ margin: '0 0 10px 0' }} className="goa-unordered-list">
          <li>
            Please make sure that you and everyone involved in the collision are
            safe.
          </li>
          <li>
            Take photos of the damaged area(s) of the vehicle and, if possible,
            the collision scene. Note: photos of the vehicle damage are a
            requirement of this online service.
          </li>
          <li>
            If your vehicle is drivable, there are no serious injuries and the
            area is safe, move your vehicle to the side of the road, out of
            traffic.
          </li>
          <li>
            Please refer to the following resource to learn more about what to
            do in the event of the collision:{' '}
            <a
              href="https://www.alberta.ca/automobile-collisions-insurance"
              target="_blank"
            >
              https://www.alberta.ca/automobile-collisions-insurance
            </a>
          </li>
        </ul>
        <h3 className={styles.headercontainer}>
          What type of collision can you report
        </h3>
        <GoASpacer vSpacing={'m'} />
        <p>
          Currently, Report My Collision only supports single vehicle collisions
          involving one driver with a class 3 licence plate (for example
          passenger vehicle). Collisions involving commercially registered
          vehicles are not accepted at this time. Collisions involving two or
          more drivers will be supported in the future. There are currently four
          types of collisions you can report using Report My Collision. All
          other types of collisions will require in-person reporting at the
          police jurisdiction where the collision occurred.
        </p>
        <h4 className={styles.headercollisiontype}>
          The four types of collisions available to report:
        </h4>
        <ul style={{ margin: '0 0 10px 0' }} className="goa-unordered-list">
          <li>Your vehicle struck an animal</li>
          <li>Your vehicle struck an object (not another vehicle)</li>
          <li>Your vehicle ran off the road</li>
          <li>Your parked vehicle was involved in a hit and run</li>
        </ul>
        <div className={styles.note}>
          <strong>Note:</strong> For a hit and run collision, if you have
          witness information or any supporting evidence, you cannot use Report
          My Collision and you must report at a police station in the
          jurisdiction where the collision occurred.
        </div>
        <GoASpacer vSpacing={'l'} />
        <GoACallout heading="Important" type="information">
          <ul style={{ margin: '0' }}>
            <li>
              If you have an Alberta.ca account, log in, agree to the Terms &
              Conditions, and check your eligibility before starting your
              collision report. You can start a report with a basic account, but
              a verified account is required to submit.
            </li>
            <li>
              If you need to register, we recommend checking your eligibility
              before creating an account. We recommend signing up for a verified
              Alberta.ca account.
            </li>
          </ul>
        </GoACallout>
        <p>Log in with your Alberta.ca account to get started</p>

        <GoABlock direction="column" mb="3xl">
          <div>
            <GoAButton
              testId="login"
              size="normal"
              width="288px"
              onClick={() => {
                handleLogin();
              }}
            >
              Log in
            </GoAButton>
          </div>
          <div>
            <GoAButton
              type="tertiary"
              trailingIcon="open"
              width="288px"
              onClick={() => {
                handleCreateNewAccountClick();
              }}
            >
              Register now
            </GoAButton>
          </div>
        </GoABlock>
        <GoAModal
          calloutVariant="important"
          heading="Before you register"
          open={openRegisterModal}
        >
          <p>
            It is highly recommended you verify you meet the criteria to use
            Report My Collision
          </p>
          <GoAButtonGroup alignment="end">
            <GoAButton
              type="tertiary"
              onClick={() => setOpenRegisterModal(false)}
            >
              Cancel
            </GoAButton>
            <GoAButton
              type="primary"
              onClick={() => {
                setOpenRegisterModal(false);
                navigate('/collision#eligibility-introduction');
              }}
            >
              Check eligibility
            </GoAButton>
          </GoAButtonGroup>
        </GoAModal>
      </div>
    </div>
  );
};
